import { colors } from 'app-v2/design-systems/theme/colors'
import { COLOR } from 'app-v2/design-tokens'
import { Chevron } from 'app/assets/icons'
import { ButtonSmall } from 'app/assets/styles'
import { Link } from 'app/common/link'
import { AlertModal } from 'app/common/modals/alert'
import { CaptionText, H4, H5, Text } from 'app/common/typography'
import { Stepper } from 'app/ecommerce/common/components'
import { Collapsible } from 'app/ecommerce/common/components/cart-summary'
import React, { Component } from 'react'
import type { IntlShape } from 'react-intl'
import { FormattedMessage, injectIntl } from 'react-intl'
import { colors as oldColors } from 'shared/assets/styles'
import type {
    ItemInfos,
    ProductCustomization,
    ProductOption,
    PurchasableItem
} from 'shared/store/ecommerce/order'
import { catalogType, formatPrice, isDefined } from 'shared/utils'
import { getMenuNameFromSize } from 'shared/utils/ecommerce'
import styled from 'styled-components'

const { text, yellow } = oldColors
const { MENU, PRODUCT_GROUP, PRODUCT } = catalogType

type Props = {
    product: PurchasableItem,
    addProduct?: (product: PurchasableItem) => void,
    deleteProduct?: (product: PurchasableItem) => void,
    setCurrentItem?: (item: PurchasableItem) => void,
    setCurrentItemInfos?: (productInfos: ItemInfos) => void,
    fromOrderDetails?: boolean,
    isAValidGame?: boolean,
    maxQuantityReached?: boolean,
    intl: IntlShape
}

type State = {
    alert: boolean
}

class CartItemComponent extends Component<Props, State> {
    constructor() {
        super()
        this.state = {
            alert: false
        }
    }

    renderHeader = enableCollapse => {
        const {
            product,
            deleteProduct,
            addProduct,
            fromOrderDetails,
            isAValidGame,
            maxQuantityReached
        } = this.props
        const {
            label,
            quantity,
            available,
            promotions,
            noIce,
            pickUpLater,
            finalPrice,
            type,
            maxQuantity,
            requiredForPromotion
        } = product
        const unavailable = available === false
        const hasPromotion = promotions && promotions.length > 0

        const name =
            fromOrderDetails && quantity > 1 ? `${label} (x${quantity})` : label
        return (
            <Header>
                <Left>
                    <StyledH4 unavailable={unavailable}>{name}</StyledH4>
                    {type === PRODUCT &&
                        isDefined(noIce) &&
                        this.renderIceChoice(noIce, true)}
                    {type === PRODUCT &&
                        isDefined(pickUpLater) &&
                        this.renderPickUpLaterChoice(pickUpLater, true)}
                    <CaptionText>
                        {formatPrice(finalPrice ? quantity * finalPrice : 0)}
                    </CaptionText>
                </Left>
                <Right>
                    {!unavailable && !fromOrderDetails && (
                        <Stepper
                            value={quantity}
                            onPressLeft={e => {
                                if (e) {
                                    e.stopPropagation()
                                }
                                if (quantity === 1) {
                                    this.setState({ alert: true })
                                } else if (deleteProduct) {
                                    deleteProduct(product)
                                }
                            }}
                            onPressRight={e => {
                                if (e) {
                                    e.stopPropagation()
                                }
                                if (
                                    !requiredForPromotion &&
                                    (!hasPromotion || isAValidGame) &&
                                    addProduct &&
                                    (!maxQuantity || !maxQuantityReached)
                                ) {
                                    addProduct({ ...product, quantity: 1 })
                                }
                            }}
                            colorRight={
                                requiredForPromotion ||
                                (hasPromotion && !isAValidGame) ||
                                maxQuantityReached
                                    ? colors.crunchyGreenDisabled
                                    : undefined
                            }
                        />
                    )}
                    <Icon>
                        {enableCollapse && (
                            <Chevron color={text} direction="down" />
                        )}
                    </Icon>
                </Right>
            </Header>
        )
    }

    renderOptions = (options: Array<ProductOption>) => {
        return options.map((option: ProductOption, index) => {
            return (
                <Text key={index}>
                    {option.quantity > 1 && option.quantity + 'x'}{' '}
                    {option.label}
                </Text>
            )
        })
    }

    renderCustomizedIngredients = (
        customizations: Array<ProductCustomization>
    ) => {
        return customizations.map((custo: ProductCustomization, index) => {
            const { label, initialQuantity, quantity, price } = custo
            const text =
                quantity > initialQuantity
                    ? 'clickandcollect.cart.custo.extra'
                    : 'clickandcollect.cart.custo.without'
            const currentQty =
                initialQuantity && quantity && quantity - initialQuantity > 0
                    ? quantity - initialQuantity
                    : quantity
            return (
                <FormattedMessage
                    id={text}
                    values={{ product: label, qty: currentQty }}
                    key={index}>
                    {(text: string) => (
                        <Text>
                            {text}
                            {!!price &&
                                (quantity > initialQuantity && price > 0
                                    ? ' (+' + formatPrice(price) + ')'
                                    : ' (' + formatPrice(price) + ')')}
                        </Text>
                    )}
                </FormattedMessage>
            )
        })
    }

    renderIceChoice = (noIce: ?boolean, header?: boolean) => {
        return (
            <div>
                <FormattedMessage
                    id={
                        noIce
                            ? 'clickandcollect.catalog.drink.no.ice'
                            : 'clickandcollect.catalog.drink.with.ice'
                    }>
                    {text =>
                        header ? (
                            <CaptionText>{text}</CaptionText>
                        ) : (
                            <Text>{text}</Text>
                        )
                    }
                </FormattedMessage>
            </div>
        )
    }

    renderPickUpLaterChoice = (pickUpLater: ?boolean, header?: boolean) => {
        return (
            <div>
                <FormattedMessage
                    id={
                        pickUpLater
                            ? 'ecommerce.catalog.pick.up.later'
                            : 'ecommerce.catalog.pick.up.now'
                    }>
                    {text =>
                        header ? (
                            <CaptionText>{text}</CaptionText>
                        ) : (
                            <Text>{text}</Text>
                        )
                    }
                </FormattedMessage>
            </div>
        )
    }

    renderContent = () => {
        const {
            product,
            setCurrentItem,
            setCurrentItemInfos,
            fromOrderDetails,
            intl
        } = this.props
        const {
            type,
            options,
            label,
            customizedIngredients,
            id,
            canBeCustomized,
            menuSize,
            noIce,
            pickUpLater
        } = product

        const size = getMenuNameFromSize(menuSize)

        return (
            <Content>
                <Line />
                {product.type === MENU && product.steps ? (
                    product.steps.map((step, index) => {
                        const {
                            label,
                            options,
                            customizedIngredients,
                            noIce,
                            pickUpLater
                        } = step
                        return (
                            <Item key={index}>
                                <Step>
                                    <H5>{label}</H5>
                                    {index === 0 && (
                                        <FormattedMessage id={size}>
                                            {(text: string) => (
                                                <StyledH5>{text}</StyledH5>
                                            )}
                                        </FormattedMessage>
                                    )}
                                </Step>
                                {!!options && this.renderOptions(options)}
                                {!!customizedIngredients &&
                                    this.renderCustomizedIngredients(
                                        customizedIngredients
                                    )}
                                {isDefined(noIce) &&
                                    this.renderIceChoice(noIce, false)}
                                {isDefined(pickUpLater) &&
                                    this.renderPickUpLaterChoice(
                                        pickUpLater,
                                        false
                                    )}
                            </Item>
                        )
                    })
                ) : (
                    <Item>
                        {!!options && this.renderOptions(options)}
                        {!!customizedIngredients &&
                            this.renderCustomizedIngredients(
                                customizedIngredients
                            )}
                    </Item>
                )}
                {!fromOrderDetails &&
                    (canBeCustomized ||
                        isDefined(noIce) ||
                        isDefined(pickUpLater)) && (
                        <StyledLink
                            url={
                                type === MENU
                                    ? intl.formatMessage({
                                          id: 'routes.clickandcollect.menu'
                                      })
                                    : type === PRODUCT
                                    ? intl.formatMessage({
                                          id: 'routes.clickandcollect.product'
                                      })
                                    : intl.formatMessage({
                                          id:
                                              'routes.clickandcollect.product.group'
                                      })
                            }
                            params={{ fromCart: true, product }}>
                            <StyledButtonMedium
                                background="white"
                                border={yellow}
                                color={text}
                                onClick={() => {
                                    if (setCurrentItem) {
                                        setCurrentItem(product)
                                    }
                                    if (setCurrentItemInfos) {
                                        setCurrentItemInfos({
                                            id:
                                                type === PRODUCT_GROUP &&
                                                product.idProductGroup
                                                    ? product.idProductGroup
                                                    : id,
                                            name: label
                                        })
                                    }
                                }}>
                                <FormattedMessage id="clickandcollect.cart.customize">
                                    {(text: string) => (
                                        <H4 color={yellow}>{text}</H4>
                                    )}
                                </FormattedMessage>
                            </StyledButtonMedium>
                        </StyledLink>
                    )}
            </Content>
        )
    }
    render() {
        const { product, fromOrderDetails, deleteProduct } = this.props
        const {
            options,
            customizedIngredients,
            available,
            canBeCustomized,
            noIce,
            pickUpLater,
            type,
            steps
        } = product
        const unavailable = available === false
        const hasCusto =
            (customizedIngredients && customizedIngredients.length > 0) ||
            (options && options.length > 0) ||
            (type === MENU && steps) ||
            (!fromOrderDetails && type === PRODUCT && isDefined(noIce)) ||
            (!fromOrderDetails && type === PRODUCT && isDefined(pickUpLater))
        const enableCollapse =
            !unavailable &&
            (!fromOrderDetails || hasCusto) &&
            (fromOrderDetails || canBeCustomized || hasCusto)

        const { alert } = this.state
        return (
            <div>
                <Collapsible
                    title={this.renderHeader(enableCollapse)}
                    content={this.renderContent()}
                    disabled={!enableCollapse}
                    unavailable={unavailable}
                />
                {alert && (
                    <AlertModal
                        alertTitleKey="clickandcollect.cart.confirmation"
                        alertContentKey="clickandcollect.cart.delete"
                        okLabel="yes"
                        leftLabel="no"
                        isOpen={alert}
                        handleClose={() => this.setState({ alert: false })}
                        onOkClick={() => {
                            this.setState({ alert: false })
                            if (deleteProduct) {
                                deleteProduct(product)
                            }
                        }}
                        onLeftClick={() => this.setState({ alert: false })}
                    />
                )}
            </div>
        )
    }
}

const Header = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    flex-wrap: wrap;
    align-items: center;
`

const Left = styled.div``

const StyledH4 = styled(H4)`
    ${props => props.unavailable && 'text-decoration-line: line-through'};
    text-transform: uppercase;
`

const Right = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
`

const Icon = styled.div`
    margin-left: 20px;
    width: 24px;
`

const Content = styled.div`
    padding: 0 24px 10px;
`

const Line = styled.div`
    width: 100%;
    height: 1px;
    background: ${COLOR.PRIMARY};
    margin-bottom: 10px;
`

const Item = styled.div`
    margin: 5px 10px;
    display: flex;
    flex-direction: column;
`

const Step = styled.div`
    display: flex;
`

const StyledH5 = styled(H5)`
    margin-left: 5px;
`

const StyledLink = styled(Link)`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
`

const StyledButtonMedium = styled(ButtonSmall)`
    display: flex;
    align-items: center;
`

export const CartItem = injectIntl(CartItemComponent)
